import { useState } from "react";
import { useAuth } from "../../hooks/use-auth";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { useToast } from "../ui/use-toast";

const SignupForm = () => {
  const { registerMutation } = useAuth();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    name: "",
    email: "",
    acceptTerms: false,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      acceptTerms: checked,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.acceptTerms) {
      toast({
        title: "Terms & Conditions",
        description: "Please accept the terms and conditions to continue.",
        variant: "destructive",
      });
      return;
    }
    
    registerMutation.mutate({
      username: formData.username,
      password: formData.password,
      name: formData.name,
      email: formData.email,
    });
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="username" className="!text-gray-900 dark:!text-gray-900">Username</Label>
          <Input
            id="username"
            name="username"
            placeholder="johnsmith"
            required
            value={formData.username}
            onChange={handleChange}
            className="!bg-white dark:!bg-white !border-gray-200 dark:!border-gray-200 !text-gray-900 dark:!text-gray-900 placeholder:!text-gray-500 dark:placeholder:!text-gray-500 focus:!ring-blue-500 dark:focus:!ring-blue-500 focus:!border-blue-500 dark:focus:!border-blue-500"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="name" className="!text-gray-900 dark:!text-gray-900">Full Name (Optional)</Label>
          <Input
            id="name"
            name="name"
            placeholder="John Smith"
            value={formData.name}
            onChange={handleChange}
            className="!bg-white dark:!bg-white !border-gray-200 dark:!border-gray-200 !text-gray-900 dark:!text-gray-900 placeholder:!text-gray-500 dark:placeholder:!text-gray-500 focus:!ring-blue-500 dark:focus:!ring-blue-500 focus:!border-blue-500 dark:focus:!border-blue-500"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="email" className="!text-gray-900 dark:!text-gray-900">Email (Optional)</Label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="john.smith@example.com"
            value={formData.email}
            onChange={handleChange}
            className="!bg-white dark:!bg-white !border-gray-200 dark:!border-gray-200 !text-gray-900 dark:!text-gray-900 placeholder:!text-gray-500 dark:placeholder:!text-gray-500 focus:!ring-blue-500 dark:focus:!ring-blue-500 focus:!border-blue-500 dark:focus:!border-blue-500"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="password" className="!text-gray-900 dark:!text-gray-900">Password</Label>
          <Input
            id="password"
            name="password"
            type="password"
            placeholder="••••••••"
            required
            minLength={6}
            value={formData.password}
            onChange={handleChange}
            className="!bg-white dark:!bg-white !border-gray-200 dark:!border-gray-200 !text-gray-900 dark:!text-gray-900 placeholder:!text-gray-500 dark:placeholder:!text-gray-500 focus:!ring-blue-500 dark:focus:!ring-blue-500 focus:!border-blue-500 dark:focus:!border-blue-500"
          />
          <p className="text-xs text-gray-500 dark:text-gray-500">
            Password must be at least 6 characters long
          </p>
        </div>
        
        <div className="flex items-start space-x-2">
          <Checkbox 
            id="acceptTerms" 
            checked={formData.acceptTerms}
            onCheckedChange={handleCheckboxChange}
            className="!bg-white dark:!bg-white !border-gray-200 dark:!border-gray-200 data-[state=checked]:!bg-blue-600 dark:data-[state=checked]:!bg-blue-600 data-[state=checked]:!border-blue-600 dark:data-[state=checked]:!border-blue-600"
          />
          <Label 
            htmlFor="acceptTerms" 
            className="text-sm font-normal leading-tight cursor-pointer !text-gray-700 dark:!text-gray-700"
          >
            I agree to the{" "}
            <a href="#" className="text-blue-600 dark:text-blue-600 hover:underline">
              Terms of Service
            </a>{" "}
            and{" "}
            <a href="#" className="text-blue-600 dark:text-blue-600 hover:underline">
              Privacy Policy
            </a>
          </Label>
        </div>
        
        <Button 
          type="submit" 
          className="w-full !bg-blue-600 hover:!bg-blue-700 !text-white dark:!bg-blue-600 dark:hover:!bg-blue-700 dark:!text-white py-3 !border-0 dark:!border-0"
          disabled={registerMutation.isPending}
        >
          {registerMutation.isPending ? "Creating account..." : "Create account"}
        </Button>
        
        {registerMutation.isError && (
          <p className="text-sm text-red-600 dark:text-red-600">
            {(registerMutation.error as Error)?.message || "An error occurred during registration."}
          </p>
        )}
      </form>
    </div>
  );
};

export default SignupForm; 